/**
 * Team-management gateway calls.
 *
 * Used to be Next.js server actions; in the SPA they are plain async
 * functions. The gateway sets the new ``kos_session`` cookie on the
 * response when a call rotates the JWT (switch / create), so the
 * caller just needs to reload the page so every loader re-runs
 * against the new tenant — there is no bearer-token plumbing on the
 * client.
 *
 * Errors are normalised into an `ActionResult` so the UI layer
 * surfaces a single shape across switch / create / invite / role /
 * remove.
 */

import { ApiError, apiRaw } from "@/lib/api";

export interface RotationResponse {
  kos_token: string;
  expires_in: number;
  user: { id: string; email: string; name: string | null };
  tenant: {
    org_id: string;
    team_id: string;
    team_slug: string;
    team_name: string;
    org_name: string;
    org_plan?: string;
    role: string;
  };
}

interface ActionOk {
  ok: true;
  /** Human-readable message for toast / aria-live regions. */
  message: string;
  /** Present when the gateway rotated the session into a team. */
  rotation?: RotationResponse;
}

interface ActionErr {
  ok: false;
  /** HTTP-ish status; `0` for "couldn't even reach the gateway". */
  status: number;
  message: string;
}

export type ActionResult = ActionOk | ActionErr;

async function readError(resp: Response): Promise<string> {
  try {
    const text = await resp.text();
    if (!text) return resp.statusText || `HTTP ${resp.status}`;
    try {
      const body = JSON.parse(text) as unknown;
      if (body && typeof body === "object") {
        // FastAPI default + our KosError envelope both put the message under `detail` or `error.message`.
        const detail = (body as { detail?: unknown }).detail;
        if (typeof detail === "string") return detail;
        if (detail && typeof detail === "object" && "message" in detail) {
          return String((detail as { message: unknown }).message);
        }
        const errBlock = (body as { error?: { message?: unknown } }).error;
        if (errBlock?.message) return String(errBlock.message);
      }
    } catch {
      // Non-JSON body (e.g. proxy 502); fall through to the raw text.
      return text;
    }
    return text;
  } catch {
    return resp.statusText || `HTTP ${resp.status}`;
  }
}

async function callGateway(
  path: string,
  init?: RequestInit,
): Promise<Response | { networkError: ApiError }> {
  try {
    return await apiRaw(path, init);
  } catch (err) {
    if (err instanceof ApiError) return { networkError: err };
    throw err;
  }
}

/** Switch the calling user into ``teamId``. The team must already include
 *  them as a member (enforced by the backend). The gateway re-mints the
 *  ``kos_session`` cookie on success — callers should reload after. */
export async function switchTeamAction(teamId: string): Promise<ActionResult> {
  const resp = await callGateway("/me/active-team", {
    method: "POST",
    body: JSON.stringify({ team_id: teamId }),
  });
  if ("networkError" in resp) {
    return { ok: false, status: 0, message: resp.networkError.message };
  }
  if (!resp.ok) {
    return { ok: false, status: resp.status, message: await readError(resp) };
  }
  const rot = (await resp.json()) as RotationResponse;
  return { ok: true, message: `Switched to ${rot.tenant.team_name}`, rotation: rot };
}

/** Create a new team in the caller's *current* org and auto-switch into it. */
export async function createTeamAction(name: string): Promise<ActionResult> {
  const trimmed = name.trim();
  if (!trimmed) {
    return { ok: false, status: 400, message: "Team name must not be empty" };
  }
  const resp = await callGateway("/teams", {
    method: "POST",
    body: JSON.stringify({ name: trimmed }),
  });
  if ("networkError" in resp) {
    return { ok: false, status: 0, message: resp.networkError.message };
  }
  if (!resp.ok) {
    return { ok: false, status: resp.status, message: await readError(resp) };
  }
  const rot = (await resp.json()) as RotationResponse;
  return { ok: true, message: `Created team ${rot.tenant.team_name}`, rotation: rot };
}

/** Add ``email`` to the current team as ``role`` (admin only on the server). */
export async function addMemberAction(email: string, role: string): Promise<ActionResult> {
  const trimmed = email.trim();
  if (!trimmed) {
    return { ok: false, status: 400, message: "Email must not be empty" };
  }
  const resp = await callGateway("/teams/current/members", {
    method: "POST",
    body: JSON.stringify({ email: trimmed, role }),
  });
  if ("networkError" in resp) {
    return { ok: false, status: 0, message: resp.networkError.message };
  }
  if (!resp.ok) {
    return { ok: false, status: resp.status, message: await readError(resp) };
  }
  return { ok: true, message: `Added ${trimmed} to the team` };
}

/** Change a member's role on the current team. */
export async function updateMemberRoleAction(userId: string, role: string): Promise<ActionResult> {
  const resp = await callGateway(`/teams/current/members/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
  if ("networkError" in resp) {
    return { ok: false, status: 0, message: resp.networkError.message };
  }
  if (!resp.ok) {
    return { ok: false, status: resp.status, message: await readError(resp) };
  }
  return { ok: true, message: `Role updated to ${role}` };
}

/** Remove a member from the current team. Self-removal is allowed (subject
 *  to the last-admin guard on the backend). */
export async function removeMemberAction(userId: string): Promise<ActionResult> {
  const resp = await callGateway(`/teams/current/members/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
  if ("networkError" in resp) {
    return { ok: false, status: 0, message: resp.networkError.message };
  }
  if (!resp.ok && resp.status !== 204) {
    return { ok: false, status: resp.status, message: await readError(resp) };
  }
  return { ok: true, message: "Member removed" };
}
